import { useRef, useState, type DragEvent } from 'react'
import { notifyError } from '../../utils/notify'

export interface ImageDropzoneProps {
  /** Aktueller Bildwert (URL oder base64 data:-URI). */
  value: string
  /** Neuer Wert nach Upload (data:-URI) bzw. '' beim Entfernen. */
  onChange: (dataUrl: string) => void
  label?: string
  hint?: string
  /** Runde Vorschau (z. B. Profilbild) statt eckig. */
  round?: boolean
}

const MAX_BYTES = 3 * 1024 * 1024 // 3 MB
// Muss zur Backend-Validierung passen (IsImageSource: data:image/(png|jpeg|svg+xml)).
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml']

/**
 * Bild-Upload per Drag&Drop oder Klick. Liest die Datei als base64 (data:-URI)
 * und meldet sie über `onChange`. Zeigt eine Vorschau und einen Entfernen-Button.
 * Optik BMC-Designsystem `console.css`; Drag&Drop-/FileReader-Logik unverändert.
 */
export function ImageDropzone({ value, onChange, label, hint, round }: ImageDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  const readFile = (file: File): void => {
    if (!file.type.startsWith('image/')) {
      notifyError('Bitte eine Bilddatei wählen.')
      return
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      notifyError('Bitte ein PNG-, JPG- oder SVG-Bild wählen.')
      return
    }
    if (file.size > MAX_BYTES) {
      notifyError('Bild zu groß (max. 3 MB).')
      return
    }
    const reader = new FileReader()
    reader.onload = () => onChange(String(reader.result))
    reader.onerror = () => notifyError('Datei konnte nicht gelesen werden.')
    reader.readAsDataURL(file)
  }

  const onDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) readFile(file)
  }

  const shape = round ? 'rounded-full' : 'rounded-[10px]'

  return (
    <div className="field">
      {label && <span className="flabel">{label}</span>}
      <div
        className="flex cursor-pointer items-center gap-3.5 px-3.5 py-3 transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/40"
        style={{
          borderRadius: 'var(--r-sm)',
          border: dragging ? '1px solid var(--blue)' : '1px dashed var(--bmc-line-2)',
          background: dragging ? 'var(--tint-blue)' : 'var(--bmc-panel-2)',
        }}
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        aria-label={label ?? 'Bild hochladen'}
      >
        {value ? (
          <img
            className={`size-16 flex-none object-cover ${shape}`}
            style={{ background: 'var(--bmc-panel)' }}
            src={value}
            alt=""
          />
        ) : (
          <span
            className={`grid size-16 flex-none place-items-center text-xs font-semibold ${shape}`}
            style={{ background: 'var(--bmc-panel)', color: 'var(--bmc-fg-3)' }}
          >
            Bild
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium" style={{ color: 'var(--bmc-fg)' }}>
            Bild hierher ziehen oder klicken
          </div>
          {hint && <div className="fhint mt-0.5">{hint}</div>}
        </div>
        {value && (
          <button
            type="button"
            className="btn ghost sm"
            onClick={(e) => {
              e.stopPropagation()
              onChange('')
            }}
          >
            Entfernen
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_TYPES.join(',')}
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) readFile(file)
          e.target.value = ''
        }}
      />
    </div>
  )
}
